import { Link } from "react-router-dom";
import { MapPin, Phone } from "lucide-react";
import { BOOKING_URL } from "@/data/site";

const CITY_PAGES = {
  Miami: "miami", Hialeah: "hialeah", "Miami Gardens": "miami-gardens", "North Miami Beach": "north-miami-beach",
  Doral: "doral", Kendall: "kendall", Aventura: "aventura", "Bal Harbour": "bal-harbour",
  "Sunny Isles Beach": "sunny-isles-beach", "Pompano Beach": "pompano-beach",
  "Coral Gables": "coral-gables", Homestead: "homestead",
  "Miami Beach": "miami-beach", Hollywood: "hollywood", "Fort Lauderdale": "fort-lauderdale",
  Miramar: "miramar", "Pembroke Pines": "pembroke-pines", "West Palm Beach": "west-palm-beach",
  "Boca Raton": "boca-raton", "Delray Beach": "delray-beach", "Boynton Beach": "boynton-beach",
  Jupiter: "jupiter",
};

const AREAS = [
  "Miami", "Hialeah", "Miami Gardens", "North Miami Beach", "Doral", "Kendall", "Coral Gables", "Homestead", "Miami Beach", "Bal Harbour",
  "Sunny Isles Beach", "Aventura", "Hollywood", "Fort Lauderdale", "Pompano Beach", "Miramar", "Pembroke Pines",
  "West Palm Beach", "Boca Raton", "Delray Beach", "Boynton Beach", "Jupiter",
];

const AreaChip = ({ name }) => {
  const slug = CITY_PAGES[name];
  const cls = "border border-zinc-200 bg-white px-4 py-3 rounded-sm flex items-center gap-2 text-sm font-heading font-bold text-brand-ink hover:border-brand hover:text-brand hover:-translate-y-0.5 transition-[transform,border-color,color] duration-200";
  const inner = (<><MapPin className="w-3.5 h-3.5 text-brand shrink-0" /> {name}</>);
  return slug
    ? <Link to={`/areas/${slug}`} data-testid={`service-area-${slug}`} className={cls}>{inner}</Link>
    : <Link to="/service-areas" data-testid={`service-area-${name.toLowerCase().replace(/\s+/g, "-")}`} className={cls}>{inner}</Link>;
};

export const HomeServiceAreas = () => (
  <section data-testid="home-service-areas" className="bg-white">
    <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-2">South Florida Service Areas</p>
      <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black text-brand-ink">Serving all of South Florida</h2>
      <p data-testid="service-areas-intro" className="text-zinc-600 mt-3 max-w-2xl leading-relaxed">
        Proudly serving South Florida homeowners and businesses across Miami-Dade, Broward and Palm Beach Counties.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        <div data-testid="county-miami-dade" className="bg-brand-ink text-white p-6 rounded-sm border-l-4 border-brand-red">
          <p className="font-heading font-black text-xl">Miami-Dade County</p>
          <p className="text-sm text-blue-100 mt-1">From Homestead to Aventura — full residential &amp; commercial coverage.</p>
        </div>
        <div data-testid="county-broward" className="bg-brand text-white p-6 rounded-sm border-l-4 border-brand-ice">
          <p className="font-heading font-black text-xl">Broward County</p>
          <p className="text-sm text-blue-100 mt-1">Miramar, Hollywood, Fort Lauderdale and everywhere in between.</p>
        </div>
        <div data-testid="county-palm-beach" className="bg-brand-dark text-white p-6 rounded-sm border-l-4 border-brand-red">
          <p className="font-heading font-black text-xl">Palm Beach County</p>
          <p className="text-sm text-blue-100 mt-1">West Palm Beach, Boca Raton, Delray Beach, Boynton Beach and everywhere in between.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-6">
        {AREAS.map((a) => <AreaChip key={a} name={a} />)}
        <Link to="/service-areas" data-testid="service-area-surrounding"
          className="border border-dashed border-zinc-300 bg-brand-subtle px-4 py-3 rounded-sm flex items-center gap-2 text-sm font-heading font-bold text-brand hover:border-brand hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
          <MapPin className="w-3.5 h-3.5 shrink-0" /> + Surrounding communities
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-8">
        <Link to="/contact" data-testid="service-areas-not-listed-link"
          className="flex items-center gap-2 text-sm text-zinc-600 hover:text-brand transition-colors duration-200 group">
          <Phone className="w-4 h-4 text-brand" />
          <span>Don't see your city? <span className="font-bold text-brand group-hover:underline underline-offset-4">Call us, we likely cover your area.</span></span>
        </Link>
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="service-areas-book-btn"
          className="bg-brand hover:bg-brand-dark text-white font-bold px-8 py-4 rounded-sm transition-colors duration-200">
          Book Service in My Area
        </a>
      </div>
    </div>
  </section>
);
